/**
 * TasksViewDialog — Fullscreen dialog wrapper for TasksView
 *
 * Opens from the sidebar Tasks menu item.
 * Thin wrapper around BaseDialog + TasksView.
 */

import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { BaseDialog } from '@modules/ui-components/dialogs/BaseDialog/BaseDialog.view';
import { TasksView } from './TasksView';
import { TASKS_PANEL } from '../selectors/tasks-panel.sel';
import type { TaskFilter, TaskRowActionsRenderer } from '../types';
import type { TaskResultActionsRenderer } from './TaskDetailDialog';

export interface TasksViewDialogProps {
  open: boolean;
  onClose: () => void;
  filter?: TaskFilter;
  renderResultActions?: TaskResultActionsRenderer;
  renderRowActions?: TaskRowActionsRenderer;
}

export const TasksViewDialog: React.FC<TasksViewDialogProps> = ({
  open,
  onClose,
  filter,
  renderResultActions,
  renderRowActions,
}) => {
  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 3,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <AssignmentIcon sx={{ color: 'text.secondary' }} />
      <Typography variant="h6" sx={{ flex: 1 }}>
        Tasks
      </Typography>
      <IconButton onClick={onClose} size="small" data-testid={TASKS_PANEL.tasksViewDialogClose}>
        <CloseIcon />
      </IconButton>
    </Box>
  );

  return (
    <BaseDialog
      open={open}
      onClose={() => onClose()}
      fullScreen
      header={header}
      data-testid={TASKS_PANEL.tasksViewDialog}
    >
      <TasksView
        filter={filter}
        renderResultActions={renderResultActions}
        renderRowActions={renderRowActions}
      />
    </BaseDialog>
  );
};
